import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, User, Calendar, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';
import { blogPosts } from '../data/blog';
import { useToast } from '../hooks/use-toast';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    if (!post) {
      navigate('/blog');
      return;
    }
    window.scrollTo(0, 0);
  }, [post, navigate]);

  if (!post) return null;

  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const nextPost = blogPosts[currentIndex + 1];
  const prevPost = blogPosts[currentIndex - 1];

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied!",
        description: "Blog post link copied to clipboard.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Back Button */}
        <Link to="/blog">
          <Button 
            variant="outline"
            className="absolute top-8 left-8 bg-white/90 border-0 hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </Link>

        {/* Post Meta */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0 mb-4">
              {post.category}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <div>
                  <p className="font-semibold">{post.author}</p>
                  <p className="text-sm text-white/70">{post.authorRole}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Share Button */}
          <div className="flex justify-end mb-8">
            <Button 
              onClick={handleShare}
              variant="outline"
              className="border-2 border-cyan-500 text-cyan-600"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share Article
            </Button>
          </div>

          {/* Article Content */}
          <Card className="border-2 mb-12">
            <CardContent className="p-8 md:p-12">
              <div 
                className="prose prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold
                  prose-h1:text-4xl prose-h1:mb-6
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-cyan-700
                  prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-teal-600
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-6 prose-li:text-gray-700
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 prose-blockquote:pl-4 prose-blockquote:italic
                  prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:p-3 prose-th:bg-cyan-50
                  prose-td:border prose-td:border-gray-300 prose-td:p-3"
                dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>') }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t">
                <span className="text-gray-600 font-semibold">Tags:</span>
                {post.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="border-cyan-500 text-cyan-600">
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* References Section */}
              {post.references && post.references.length > 0 && (
                <div className="mt-12 pt-8 border-t">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">References & Educational Resources</h3>
                  
                  {/* YouTube Videos */}
                  {post.references.filter(ref => ref.type === 'video').length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-xl font-semibold text-gray-900 mb-4">📺 Educational Videos</h4>
                      <div className="grid md:grid-cols-2 gap-6">
                        {post.references.filter(ref => ref.type === 'video').map((ref, idx) => {
                          // Extract YouTube video ID
                          const videoId = ref.url.includes('youtube.com') ? ref.url.split('v=')[1]?.split('&')[0] : null;
                          return videoId ? (
                            <div key={idx} className="space-y-2">
                              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                                <iframe
                                  width="100%"
                                  height="100%"
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={ref.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="w-full h-full"
                                ></iframe>
                              </div>
                              <div>
                                <a 
                                  href={ref.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-cyan-600 hover:text-cyan-700 font-semibold hover:underline"
                                >
                                  {ref.title}
                                </a>
                                <p className="text-sm text-gray-600">{ref.source}</p>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Articles & Research */}
                  {post.references.filter(ref => ref.type === 'article').length > 0 && (
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-4">📚 Articles & Research</h4>
                      <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
                        {post.references.filter(ref => ref.type === 'article').map((ref, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                              {idx + 1}
                            </div>
                            <div className="flex-grow">
                              <a 
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-600 hover:text-cyan-700 font-semibold hover:underline"
                              >
                                {ref.title}
                              </a>
                              <p className="text-sm text-gray-600 mt-1">{ref.source}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-500 mt-6 italic">
                    Note: External links and videos open in a new tab. We are not responsible for the content of external websites.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {prevPost && (
              <Link to={`/blog/${prevPost.slug}`}>
                <Card className="border-2 hover:border-cyan-400 transition-all duration-300 h-full cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-cyan-600 mb-2">
                      <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                      <span className="text-sm font-semibold">Previous Article</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-cyan-600 transition-colors">
                      {prevPost.title}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            )}
            
            {nextPost && (
              <Link to={`/blog/${nextPost.slug}`} className={!prevPost ? 'md:col-start-2' : ''}>
                <Card className="border-2 hover:border-cyan-400 transition-all duration-300 h-full cursor-pointer group">
                  <CardContent className="p-6 text-right">
                    <div className="flex items-center justify-end gap-2 text-cyan-600 mb-2">
                      <span className="text-sm font-semibold">Next Article</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-cyan-600 transition-colors">
                      {nextPost.title}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>

          {/* CTA */}
          <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Experience These Benefits?
              </h3>
              <p className="text-gray-600 mb-6">
                Get your Niloman HX Smart RO system and transform your health today
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/shop')}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  Shop Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="border-2 border-cyan-500 text-cyan-600"
                >
                  Book Free Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
